import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { toast } from 'react-toastify'
import { SortType } from 'rsuite-table/lib/@types/common'
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'

import { StateParamsModel } from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as reportMenu from '../index'
import AddReportMenu from './AddReportMenu'

const ReportMenuComponent = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState('Add')
  const [editSelectedData, setEditSelectedData] = useState<any>()
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [alertOpen, setAlertOpen] = useState(false)

  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
  })

  const {
    data,
    loading,
    singleEnableSuccess,
    singleDisableSuccess,
    enableSuccess,
    disableSuccess,
    deleteSuccess,
    toggleLoading,
  } = useSelector((state: any) => state.reportMenu)

  const Cell = Table.Cell

  const ActionCell = ({ rowData, dataKey, ...props }: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => {
              setEditSelectedData(rowData)
              setActionType('Edit')
              setOpen(true)
            }}
            icon={<Edit2 />}
            data-cy='edit-button'
          />
        </Whisper>
      </CheckPermissions>
      <CheckPermissions type='Delete'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => {
              setAlertOpen(true)
              setCheckedValues([rowData.id])
            }}
            icon={<TrashIcon />}
            data-cy='delete-button'
          />
        </Whisper>
      </CheckPermissions>
      {/* <Whisper placement='top' trigger='hover' speaker={<Tooltip>Status</Tooltip>}>
        <Toggle
          size='sm'
          disabled={toggleLoading}
          checked={rowData.status}
          onClick={() => handleToggleAction(rowData)}
          data-cy='toggle-button'
        />
      </Whisper> */}
    </Cell>
  )

  const handleToggleAction = (data: { [key: string]: any }) => {
    data?.status
      ? dispatch(reportMenu.actions.singleDisableReportMenu({ id: data.id }))
      : dispatch(reportMenu.actions.singleEnableReportMenu({ id: data.id }))
  }

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Title',
      dataKey: 'title',
      flexGrow: 1,
      cell: <Cell dataKey='title' />,
      sortable: true,
    },
    {
      label: 'Slug',
      dataKey: 'slug',
      flexGrow: 1,
      cell: <Cell dataKey='slug' />,
      sortable: true,
    },
    {
      label: 'Embedded ID',
      dataKey: 'embedded_id',
      flexGrow: 2,
      cell: <Cell dataKey='embedded_id' />,
      sortable: false,
    },
    {
      label: 'Roles',
      dataKey: 'roles',
      flexGrow: 2,
      cell: (
        <Cell>
          {(rowData) =>
            Array.isArray(rowData?.roles) && rowData.roles.length > 0
              ? rowData.roles.map((r: string) => (
                  <Tag key={r} color='blue' size='sm' style={{marginRight: 4}}>
                    {r}
                  </Tag>
                ))
              : '—'
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 90,
      cell: (
        <Cell>
          {(rowData) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>Active</Tag>
            ) : (
              <Tag color='red' size='sm'>Inactive</Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  useEffect(() => {
    dispatch(reportMenu.actions.getReportMenuData(params))
  }, [params])

  useEffect(() => {
    if (deleteSuccess) toast.success('Report Menu deleted successfully')
    if (enableSuccess) toast.success('Report Menu enabled successfully')
    if (disableSuccess) toast.success('Report Menu disabled successfully')
    if (deleteSuccess || enableSuccess || disableSuccess) {
      setCheckedValues([])
      dispatch(reportMenu.actions.getReportMenuData(params))
    }
  }, [deleteSuccess, enableSuccess, disableSuccess])

  useEffect(() => {
    if (singleEnableSuccess) toast.success('Report Menu enabled successfully')
    if (singleDisableSuccess) toast.success('Report Menu disabled successfully')
    setCheckedValues([])
  }, [singleEnableSuccess, singleDisableSuccess])

  const tableData = data?.superset_embed_configs
    ? data.superset_embed_configs.map((item: any, i: number) => ({
      sn: (params.page - 1) * params.limit + (i + 1),
      id: item.id,
      title: item.dashboard_title,
      slug: item.dashboard_slug,
      embedded_id: item.embed_uuid,
      status: item.is_active,
      roles: item.roles || [],
    }))
    : []

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({ ...params, orderBy: dataKey, order: sortType })
  }

  const handleDelete = () => {
    dispatch(reportMenu.actions.deleteReportMenu(checkedValues))
    setAlertOpen(false)
  }

  const enableBulk = () => {
    if (!isEmpty(checkedValues)) {
      dispatch(reportMenu.actions.enableReportMenu(checkedValues.map((id) => ({ id }))))
    } else {
      toast.error('No data selected')
    }
  }

  const disableBulk = () => {
    if (!isEmpty(checkedValues)) {
      dispatch(reportMenu.actions.disableReportMenu(checkedValues.map((id) => ({ id }))))
    } else {
      toast.error('No data selected')
    }
  }

  return (
    <div className='shadow p-3 bg-white rounded'>
      <DesignComponent
        moduleName='Report Menu'
        params={params}
        setParams={setParams}
        handleRefresh={() => dispatch(reportMenu.actions.getReportMenuData(params))}
        handleAddModal={() => {
          setEditSelectedData({})
          setActionType('Add')
          setOpen(true)
        }}
        handleAlertOpen={() => setAlertOpen(true)}
        exportShow={false}
        importShow={false}
        deleteShow={false}
        addShow={true}
        toggleMultipleShow={false}
        templateLinkShow={false}
      />

      <div className='datatable'>
        <RSuiteTable
          onChecked={setCheckedValues}
          showCheckbox={true}
          columns={columns}
          data={tableData}
          checkedValues={checkedValues}
          showLoading={loading}
          handleSort={handleSort}
        />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          className='mt-5'
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={data?.meta?.total || 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params.limit}
          activePage={params.page}
          onChangePage={(value) => setParams({ ...params, page: value })}
          onChangeLimit={(dataKey) => setParams({ ...params, limit: dataKey })}
        />

        {open && (
          <AddReportMenu
            open={open}
            params={params}
            handleClose={() => setOpen(false)}
            actionType={actionType}
            editSelectedData={editSelectedData}
          />
        )}

        {alertOpen &&
          (!isEmpty(checkedValues) ? (
            <DeleteModal
              handleClick={handleDelete}
              isOpen={alertOpen}
              handleClose={() => setAlertOpen(false)}
            />
          ) : (
            toast.error('No data selected') && setAlertOpen(false)
          ))}
      </div>
    </div>
  )
}

export default ReportMenuComponent
